import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, LabelList, Cell,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, HelpCircle, Users, Crown, ArrowDown, ArrowUp, ArrowDownRight, ArrowUpRight, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CHURCH_COLORS = [
  "#17365D", // Siryon — deep navy
  "#E5B52A", // El Olam — gold
  "#166534", // Semua Umat — green
  "#7c3aed", // Air Hidup — purple
  "#dc2626", // Hosana — red
  "#0891b2", // Sion — cyan
  "#f97316", // PBC — orange
  "#6b7280", // Hermon — gray (dashed line saat proyeksi)
];

const STATUS_META = {
  MENINGKAT: { label: "Meningkat", color: "#16a34a", Icon: ArrowUpRight, bg: "bg-emerald-50", text: "text-emerald-900", border: "border-emerald-200" },
  STABIL: { label: "Stabil", color: "#6b7280", Icon: ArrowRight, bg: "bg-slate-50", text: "text-slate-900", border: "border-slate-200" },
  MENURUN: { label: "Menurun", color: "#dc2626", Icon: ArrowDownRight, bg: "bg-red-50", text: "text-red-900", border: "border-red-200" },
  "BELUM ADA DATA PEMBANDING": { label: "Belum Ada Pembanding", color: "#f59e0b", Icon: HelpCircle, bg: "bg-amber-50", text: "text-amber-900", border: "border-amber-200" },
  "BELUM ADA DATA": { label: "Belum Ada Data", color: "#9ca3af", Icon: HelpCircle, bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" },
};

export function GrowthSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);
  const [view, setView] = useState("current"); // current | trend | projection
  const [sortMode, setSortMode] = useState("desc"); // name | desc

  const load = async (targetYear) => {
    setLoading(true);
    try {
      const url = targetYear ? `/dashboard/growth?year=${targetYear}` : "/dashboard/growth";
      const { data } = await api.get(url);
      setData(data);
      if (!year) setYear(data.current_year);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (year) load(year); /* eslint-disable-next-line */ }, [year]);

  const barData = useMemo(() => {
    if (!data) return [];
    let arr = data.churches.map((c, i) => ({
      key: c.code,
      name: c.short_name,
      full: c.name,
      value: c.current_member_count,
      display: c.current_member_count == null ? "Belum" : c.current_member_count,
      color: CHURCH_COLORS[i] || "#17365D",
      status: c.status,
    }));
    if (sortMode === "desc") {
      arr = arr.slice().sort((a, b) => {
        if (a.value == null && b.value == null) return 0;
        if (a.value == null) return 1;
        if (b.value == null) return -1;
        return b.value - a.value;
      });
    }
    return arr;
  }, [data, sortMode]);

  const lineData = useMemo(() => {
    if (!data) return { rows: [], keys: [] };
    const years = data.year_range;
    const rows = years.map((y) => {
      const row = { year: y };
      data.churches.forEach((c) => {
        const actual = c.actual.find((p) => p.year === y);
        const proj = c.projection.find((p) => p.year === y);
        // Actual: kalau ada, tulis nilai + null proyeksi
        if (actual && actual.member_count != null) {
          row[`${c.short_name}__actual`] = actual.member_count;
          row[`${c.short_name}__proj`] = null;
        } else if (proj) {
          row[`${c.short_name}__actual`] = null;
          row[`${c.short_name}__proj`] = proj.member_count;
        } else {
          row[`${c.short_name}__actual`] = null;
          row[`${c.short_name}__proj`] = null;
        }
      });
      return row;
    });
    return { rows, keys: data.churches.map((c) => c.short_name) };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Memuat data perkembangan…
      </div>
    );
  }
  if (!data) return null;

  return (
    <section className="space-y-4" data-testid="growth-section">
      {/* Header + Filter */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-accent font-bold">Perkembangan Pelayanan</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl mt-1">Perkembangan 8 Jemaat GPDP Klasis Mimika</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Data resmi VERIFIED dari Sekretariat Klasis. Proyeksi menggunakan CAGR bila tersedia ≥ 2 periode data.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year || data.current_year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[110px]" data-testid="filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2026, 2027, 2028, 2029, 2030, 2031].map((y) => (
                <SelectItem key={y} value={String(y)} disabled={!data.available_years.includes(y) && y !== data.current_year}>
                  {y}{data.available_years.includes(y) ? "" : " (kosong)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Ringkasan */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        <RingkasCard icon={Users} label="Total Jiwa Klasis" value={data.total_current.toLocaleString("id-ID")} sub={`Tahun ${data.current_year}`} tone="primary" testid="stat-total-jiwa" />
        <RingkasCard icon={Crown} label="Jemaat Terbesar" value={data.largest ? data.largest.count : "-"} sub={data.largest ? data.largest.name.replace("GPDP Jemaat ", "") : "Belum ada"} tone="accent" testid="stat-largest" />
        <RingkasCard icon={ArrowDown} label="Jemaat Terkecil" value={data.smallest ? data.smallest.count : "-"} sub={data.smallest ? data.smallest.name.replace("GPDP Jemaat ", "") : "Belum ada"} tone="muted" testid="stat-smallest" />
        <RingkasCard icon={ArrowUpRight} label="Meningkat" value={`${data.counts.meningkat} / ${data.counts.total_jemaat}`} sub="Vs tahun lalu" tone="success" testid="stat-meningkat" />
        <RingkasCard icon={Minus} label="Stabil" value={`${data.counts.stabil} / ${data.counts.total_jemaat}`} sub="Vs tahun lalu" tone="muted" testid="stat-stabil" />
        <RingkasCard icon={ArrowDownRight} label="Menurun" value={`${data.counts.menurun} / ${data.counts.total_jemaat}`} sub="Vs tahun lalu" tone="danger" testid="stat-menurun" />
      </div>

      {/* View Toggle */}
      <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
        <TabBtn active={view === "current"} onClick={() => setView("current")} testid="tab-current">Jumlah Saat Ini</TabBtn>
        <TabBtn active={view === "trend"} onClick={() => setView("trend")} testid="tab-trend">Perkembangan 2026–2031</TabBtn>
        <TabBtn active={view === "projection"} onClick={() => setView("projection")} testid="tab-projection">Proyeksi 2031</TabBtn>
      </div>

      {/* Chart Body */}
      {view === "current" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <h3 className="font-heading font-bold text-base">Jumlah Jiwa 8 Jemaat GPDP Klasis Mimika ({data.current_year})</h3>
              <div className="text-xs text-muted-foreground mt-0.5">Data VERIFIED. Hermon: Belum Ada Data.</div>
            </div>
            <div className="flex gap-1">
              <TabBtn small active={sortMode === "name"} onClick={() => setSortMode("name")} testid="sort-name">Urutan Nama</TabBtn>
              <TabBtn small active={sortMode === "desc"} onClick={() => setSortMode("desc")} testid="sort-desc">Terbesar → Terkecil</TabBtn>
            </div>
          </CardHeader>
          <CardContent>
            <div className="w-full h-72 sm:h-80">
              <ResponsiveContainer>
                <BarChart data={barData} margin={{ top: 24, right: 12, left: -14, bottom: 42 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={54} />
                  <YAxis tick={{ fontSize: 10 }} width={38} />
                  <Tooltip content={<CurrentBarTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    {barData.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                    <LabelList dataKey="display" position="top" style={{ fontSize: 10, fontWeight: 700 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {view === "trend" && (
        <Card>
          <CardHeader className="pb-2">
            <h3 className="font-heading font-bold text-base">Perkembangan Jemaat 2026–2031</h3>
            <div className="text-xs text-muted-foreground">Garis solid = data VERIFIED. Garis putus-putus = proyeksi (butuh ≥2 periode).</div>
          </CardHeader>
          <CardContent>
            <div className="w-full h-96">
              <ResponsiveContainer>
                <LineChart data={lineData.rows} margin={{ top: 16, right: 16, left: -14, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} width={38} />
                  <Tooltip content={<TrendTooltip churches={data.churches} />} />
                  <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                  {lineData.keys.map((k, i) => (
                    <React.Fragment key={k}>
                      <Line
                        type="monotone" dataKey={`${k}__actual`} name={k}
                        stroke={CHURCH_COLORS[i] || "#17365D"} strokeWidth={2.5}
                        dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone" dataKey={`${k}__proj`} name={`${k} (proyeksi)`}
                        stroke={CHURCH_COLORS[i] || "#17365D"} strokeWidth={1.5}
                        strokeDasharray="6 4" dot={{ r: 2 }} connectNulls legendType="none"
                        isAnimationActive={false}
                      />
                    </React.Fragment>
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {view === "projection" && <ProjectionView data={data} />}

      {/* Tabel Status Perkembangan */}
      <Card>
        <CardHeader className="pb-2">
          <h3 className="font-heading font-bold text-base">Status Perkembangan {data.current_year}</h3>
          <div className="text-xs text-muted-foreground">Perubahan vs tahun sebelumnya. Ambang: ±1% = stabil.</div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="growth-table">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Jemaat</th>
                <th className="text-right px-3 py-2">Sebelumnya</th>
                <th className="text-right px-3 py-2">Saat Ini</th>
                <th className="text-right px-3 py-2">Selisih</th>
                <th className="text-right px-3 py-2">Perubahan</th>
                <th className="text-center px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.churches.map((c) => {
                const meta = STATUS_META[c.status] || STATUS_META["BELUM ADA DATA"];
                const Icon = meta.Icon;
                return (
                  <tr key={c.church_id} className="border-t border-border" data-testid={`growth-row-${c.code}`}>
                    <td className="px-3 py-2 font-medium">{c.name.replace("GPDP Jemaat ", "")}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.previous_member_count ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{c.current_member_count ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.delta != null ? (c.delta > 0 ? `+${c.delta}` : c.delta) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.pct_change != null ? `${c.pct_change > 0 ? "+" : ""}${c.pct_change}%` : "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.bg} ${meta.text} ${meta.border}`}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </section>
  );
}

function RingkasCard({ icon: Icon, label, value, sub, tone, testid }) {
  const toneClass = {
    primary: "bg-primary/5 text-primary border-primary/20",
    accent: "bg-accent/10 text-accent border-accent/25",
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    danger: "bg-red-50 text-red-800 border-red-200",
    muted: "bg-muted/40 text-foreground border-border",
  }[tone] || "bg-muted/40 border-border";
  return (
    <div className={`rounded-xl border p-2.5 sm:p-3 ${toneClass}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-1 mb-1">
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70 leading-tight">{label}</div>
        <Icon className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
      </div>
      <div className="font-heading font-black text-lg sm:text-xl leading-tight">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5 line-clamp-1">{sub}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, small }) {
  return (
    <button
      role="tab" onClick={onClick}
      className={`px-3 ${small ? "py-1 text-[11px]" : "py-2 text-xs"} font-semibold border-b-2 -mb-px transition ${
        active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
      data-testid={testid}
    >{children}</button>
  );
}

function CurrentBarTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-md shadow-lg p-2 text-xs" data-testid="bar-tooltip">
      <div className="font-bold">{p.full}</div>
      <div className="text-muted-foreground">
        {p.value == null ? "Belum ada data terverifikasi" : <><span className="font-mono font-bold">{p.value}</span> jiwa</>}
      </div>
    </div>
  );
}

function TrendTooltip({ active, payload, label, churches }) {
  if (!active || !payload || !payload.length) return null;
  // Merge actual & proj menjadi 1 entri per church
  const map = {};
  payload.forEach((p) => {
    const [name, kind] = p.dataKey.split("__");
    if (!map[name]) map[name] = { color: p.color };
    map[name][kind] = p.value;
  });
  const rows = Object.entries(map).filter(([, v]) => v.actual != null || v.proj != null);
  return (
    <div className="bg-background border border-border rounded-md shadow-lg p-2 text-xs max-w-xs" data-testid="trend-tooltip">
      <div className="font-bold mb-1">Tahun {label}</div>
      {rows.map(([name, v]) => (
        <div key={name} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full inline-block" style={{backgroundColor: v.color}} />
          <span className="flex-1">{name}</span>
          <span className="font-mono font-bold">{v.actual ?? v.proj}</span>
          {v.proj != null && <span className="text-[10px] italic text-muted-foreground">(proyeksi)</span>}
        </div>
      ))}
    </div>
  );
}

function ProjectionView({ data }) {
  const withProjection = data.churches.filter((c) => c.projection.length > 0);
  if (withProjection.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <HelpCircle className="h-10 w-10 mx-auto text-amber-500" />
          <div className="font-heading font-bold text-base mt-3">Proyeksi belum tersedia</div>
          <div className="text-sm text-muted-foreground mt-1">Dibutuhkan minimal <strong>dua periode</strong> data terverifikasi untuk menghitung CAGR. Saat ini baru tersedia data tahun {data.available_years.join(", ") || "—"}.</div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <h3 className="font-heading font-bold text-base">Proyeksi 2031 (CAGR)</h3>
        <div className="text-xs text-muted-foreground">Compound Annual Growth Rate dari data terverifikasi. Hanya indikatif, bukan target.</div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Jemaat</th>
              <th className="text-right px-3 py-2">Saat Ini</th>
              <th className="text-right px-3 py-2">Proyeksi 2031</th>
              <th className="text-right px-3 py-2">Perubahan</th>
            </tr>
          </thead>
          <tbody>
            {withProjection.map((c) => {
              const last = c.projection.find((p) => p.year === 2031) || c.projection[c.projection.length - 1];
              const cur = c.current_member_count;
              const growth = cur ? Math.round(((last.member_count - cur) / cur) * 100) : null;
              return (
                <tr key={c.church_id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{c.name.replace("GPDP Jemaat ", "")}</td>
                  <td className="px-3 py-2 text-right font-mono">{cur}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{last.member_count}</td>
                  <td className="px-3 py-2 text-right font-mono">{growth != null ? `${growth > 0 ? "+" : ""}${growth}%` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
